import React, { useContext, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { getCheapestPackagesByRoom } from 'selectors/offerPage/bedbankMultipleRoomBookingSelectors'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { createOfferStructuredData } from 'components/utils/OfferStructuredData'

interface Props {
  offer: App.BedbankOffer
}

function HotelStructuredData(props: Props) {
  const { offer } = props
  const utmBp = useAppSelector(state => state.utm.bp)
  const geo = useContext(GeoContext)
  const contextCurrency = useContext(CurrencyContext)
  const { checkIn, checkOut } = useAppSelector((state) => state.router.location.query)
  const offerPageState = useContext(OfferPageStateContext)

  const cheapestPackageSelectionsByRoom = useAppSelector(appState => getCheapestPackagesByRoom(offerPageState, appState))

  const currency = contextCurrency ?? geo.currentCurrency

  const pricingData = useMemo(() => {
    if (utmBp !== '3' || !checkIn || !checkOut) {
      return {}
    }

    // if utm_bp=3, Get the hotelOnly key and extract the rate object
    const hotelOnly = cheapestPackageSelectionsByRoom?.hotelOnly
    if (!hotelOnly || typeof hotelOnly !== 'object') {
      return {}
    }

    const hotelOnlyKeys = Object.keys(hotelOnly)
    const firstKey = hotelOnlyKeys[0]
    if (!firstKey) {
      return {}
    }

    const firstSelection = hotelOnly[firstKey]
    const totals = firstSelection?.rate?.totals

    if (!totals?.total || totals.total === 0) {
      return {}
    }

    const { propertyFees, total = 0, exclusive, taxesAndFees } = totals

    return {
      makesOffer: createOfferStructuredData(checkIn, checkOut, {
        propertyFees,
        offerPrice: total,
        offerPriceBeforeTax: exclusive,
        taxesAndFees,
      }, currency)
    }
  }, [utmBp, checkIn, checkOut, cheapestPackageSelectionsByRoom, currency])

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    name: offer.name,
    description: offer.metaDescription,
    identifier: offer.id,
    address: {
      '@type': 'PostalAddress',
      addressCountry: offer.property.address.countryCode,
      addressRegion: offer.property.address.stateProvinceName,
      addressLocality: offer.property.address.city,
      postalCode: offer.property.address.postalCode,
      streetAddress: offer.property.address.line1 + (offer.property.address.line2 ? ' ' + offer.property.address.line2 : ''),
    },
    ...pricingData,
    telephone: offer.property.phone,
  }), [offer, pricingData])

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  )
}

export default React.memo(HotelStructuredData)
